import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { JobRunsTimeline } from "@/components/engine/JobRunsTimeline";
import { JobRunLiveSheet } from "@/components/engine/JobRunLiveSheet";
import { listRuns, getJob } from "@/api/engine";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions } from "@/hooks/usePermissions";
import { formatNumber } from "@/lib/formatters";
import type { JobConfig, JobRun } from "@/api/types";

const PAGE_SIZE = 50;
/** Status keys that mean "still tailing" — drives the auto-refresh
 *  cadence so the table badges flip from running → succeeded /
 *  failed without a manual refresh. Mirrors JobRunLiveSheet's
 *  IN_FLIGHT_STATUSES. */
const IN_FLIGHT_STATUSES = new Set(["running", "pending", "started"]);
const RUNS_REFRESH_MS = 4000;

type LoadStatus = "loading" | "ok" | "error";

export default function JobRunsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { project } = useCurrentProject();
  const canViewEngine = usePermissions().canViewEngine;
  const slug = project?.slug ?? null;

  const [job, setJob] = useState<JobConfig | null>(null);
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [activeRun, setActiveRun] = useState<JobRun | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    if (!slug || !jobId) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const [jobData, runsRes] = await Promise.all([
        getJob(slug, jobId),
        listRuns(slug, jobId, page, PAGE_SIZE),
      ]);
      setJob(jobData);
      setRuns(runsRes.items);
      setTotal(runsRes.total);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractMessage(err));
      setStatus("error");
    }
  }, [slug, jobId, page]);

  /** Background refresh — same fetch as load() but skips the loading
   *  state so the table doesn't flash the skeleton on each in-flight
   *  poll, and swallows transient errors so a flaky network doesn't
   *  unmount the table. */
  const refresh = useCallback(async () => {
    if (!slug || !jobId) return;
    try {
      const runsRes = await listRuns(slug, jobId, page, PAGE_SIZE);
      setRuns(runsRes.items);
      setTotal(runsRes.total);
    } catch {
      // keep the last good state
    }
  }, [slug, jobId, page]);

  useEffect(() => {
    if (canViewEngine) void load();
  }, [load, canViewEngine]);

  // Auto-refresh while any run is in flight so the table badges flip
  // live. Stops as soon as every visible run has settled to a terminal
  // status — `hasInFlight` is a boolean memo so the effect only re-runs
  // when the answer actually changes.
  const hasInFlight = useMemo(
    () => runs.some((r) => IN_FLIGHT_STATUSES.has(r.status)),
    [runs],
  );
  useEffect(() => {
    if (!hasInFlight) return;
    const handle = setInterval(() => void refresh(), RUNS_REFRESH_MS);
    return () => clearInterval(handle);
  }, [hasInFlight, refresh]);

  useEffect(() => {
    if (slug && !canViewEngine) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, canViewEngine, navigate]);

  if (!canViewEngine) {
    return null;
  }

  function pickRun(run: JobRun) {
    setActiveRun(run);
    setDetailOpen(true);
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center gap-2 text-sm">
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="text-slate-500 hover:text-slate-900"
        >
          <Link to={`/projects/${slug}/engine`}>
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Engine
          </Link>
        </Button>
      </div>

      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {job?.name ?? jobId}
          </h1>
          <p className="text-sm text-slate-500">
            {job ? (
              <>
                <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600">
                  {job.cron_expression}
                </code>
                <span className="ml-2">{job.job_id}</span>
              </>
            ) : (
              "Loading…"
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={status === "loading"}
        >
          <RefreshCcw
            className={
              status === "loading"
                ? "mr-2 h-3.5 w-3.5 animate-spin"
                : "mr-2 h-3.5 w-3.5"
            }
          />
          Refresh
        </Button>
      </header>

      {status === "error" && (
        <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-rose-700">
              Couldn&rsquo;t load runs
            </p>
            {errorMessage && (
              <p className="text-xs text-rose-600">{errorMessage}</p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
          >
            <RefreshCcw className="mr-2 h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      )}

      {status === "loading" ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-xl bg-white shadow-card-rest"
            />
          ))}
        </div>
      ) : (
        <>
          <JobRunsTimeline
            runs={runs}
            onPick={pickRun}
            selectedRunId={activeRun?.id ?? null}
          />

          {runs.length > 0 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                <span className="font-medium text-slate-700">
                  {rangeStart}–{rangeEnd}
                </span>{" "}
                of {formatNumber(total)}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="px-2 text-slate-600">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {slug && jobId && (
        <JobRunLiveSheet
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          projectSlug={slug}
          jobId={jobId}
          jobName={job?.name ?? jobId}
          mode="historical"
          runId={activeRun?.id ?? null}
        />
      )}
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
